import { Fragment } from 'react'
import {
  Alert,
  Badge,
  Code,
  Group,
  Paper,
  Stack,
  Table,
  Text,
  Title,
  Tooltip,
} from '@mantine/core'
import { isStaleBuild, ago, type JobHealth, type JobRun, type JobState } from '@racedash/shared'
import StatusDot from '../components/StatusDot'
import {
  useLatestJobRuns,
  useDeployedBuild,
  useCapturePlan,
  useCaptureRuns,
  useNow,
  useOpsHealth,
  useRisChannels,
  useSentAlerts,
  useWindowRuns,
  type CaptureRun,
  type PlannedWindow,
} from '../data/ops'

/**
 * The operations view.
 *
 * Three questions, in the order they matter:
 *
 *   1. Is the machinery alive?     — the fleet, and its heartbeats
 *   2. Is anything running now?    — open windows, and what they are producing
 *   3. Will the next race work?    — the forward plan, and what it cannot cover
 *
 * The order is deliberate. Everything below the first question is only worth
 * reading if the answer to it is yes: a page showing "nothing is capturing" is
 * reassuring when the reconciler is alive and meaningless when it is not, and
 * those two used to look identical.
 *
 * The consistent failure this page is built against is that silence reads as
 * health. So absences are rendered as loudly as errors: a declared job with no
 * runs gets a red row rather than no row, and an attached event that produces
 * no capture windows is a warning rather than an empty space in a list.
 */
export default function Status() {
  const now = useNow()
  const health = useOpsHealth(now)
  const plan = useCapturePlan()
  const captures = useCaptureRuns()
  const channels = useRisChannels()
  const alerts = useSentAlerts()

  const upcoming = (plan?.windows ?? []).filter((w) => w.end > now)
  const liveChannels = (channels ?? []).filter((c) => c.fresh)

  return (
    <Stack gap="xl">
      <div>
        <Title order={1} size="h2">
          Status
        </Title>
        <Text c="dimmed" size="sm">
          What is running, what is going to run, and what is not working.
        </Text>
      </div>

      <DeployedBuildLine now={now} />

      <Verdict health={health} now={now} />

      {health.problems.length > 0 && (
        <Alert color="red" variant="light" title="Needs attention">
          <Stack gap={6}>
            {health.problems.map((p) => (
              <Text key={p} size="sm">
                {p}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* ------------------------------------------------------------ jobs */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Jobs
          </Title>
          <Text c="dimmed" size="sm">
            Every job that runs without anybody asking. The list is fixed rather than built from
            what has reported, so a job that has never run once shows as a fault instead of as
            nothing at all.
          </Text>
        </div>

        <Paper withBorder>
          <Table.ScrollContainer minWidth={760}>
            <Table verticalSpacing="xs" horizontalSpacing="sm">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th w={150}>Job</Table.Th>
                  <Table.Th w={100}>State</Table.Th>
                  <Table.Th w={110}>Last run</Table.Th>
                  <Table.Th>What it said</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {health.jobs.map((h) => (
                  <JobRow key={h.spec.id} health={h} now={now} />
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Paper>
      </Stack>

      {/* ------------------------------------------------------------- now */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Capturing now
          </Title>
          <Text c="dimmed" size="sm">
            An execution being alive is not the same claim as data arriving. The counters are the
            second claim — a capture wedged on a failing fetch looks perfectly healthy from
            outside the process.
          </Text>
        </div>

        {health.openWindows.length === 0 ? (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center" size="sm">
              No capture window is open. {nextWindowLine(upcoming, now)}
            </Text>
          </Paper>
        ) : (
          <Stack gap="sm">
            {health.openWindows.map((w) => (
              <OpenWindow key={w.id} window={w} now={now} captures={captures ?? []} />
            ))}
          </Stack>
        )}

        {liveChannels.length > 0 && (
          <Paper withBorder p="md">
            <Text size="sm" fw={600} mb={6}>
              RIS channels carrying a live session
            </Text>
            <Stack gap={4}>
              {liveChannels.map((c) => (
                <Text key={c.id} size="sm">
                  <Code>{c.path}</Code> {c.seriesName || 'unnamed series'} — {c.sessionName} (
                  {c.cars} cars, {c.status})
                </Text>
              ))}
            </Stack>
          </Paper>
        )}
      </Stack>

      {/* ------------------------------------------------------------ next */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Coming up
          </Title>
          <Text c="dimmed" size="sm">
            The reconciler&rsquo;s own plan, republished every five minutes, over{' '}
            {plan?.horizonDays ?? 21} days — not a second guess at it. It acts on the next 36
            hours and reports the rest, which is what makes &ldquo;will Saturday work&rdquo; a
            question you can ask on a Tuesday.
          </Text>
        </div>

        {plan && !plan.generatedAt && (
          <Alert color="red" variant="light">
            No plan has ever been published. Either the reconciler has not run since this was
            deployed, or it cannot write to <Code>ops/capturePlan</Code>.
          </Alert>
        )}

        {(plan?.skips.length ?? 0) > 0 && (
          <Alert color="orange" variant="light" title="Attached, but nothing will be captured">
            <Stack gap={6}>
              {plan?.skips.map((s) => (
                <Text key={s.eventId} size="sm">
                  {s.detail}
                </Text>
              ))}
            </Stack>
          </Alert>
        )}

        {upcoming.length === 0 ? (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center" size="sm">
              Nothing to capture in the next {plan?.horizonDays ?? 21} days.
            </Text>
          </Paper>
        ) : (
          <Paper withBorder>
            <Table.ScrollContainer minWidth={720}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={200}>When</Table.Th>
                    <Table.Th>Event</Table.Th>
                    <Table.Th w={150}>Requested by</Table.Th>
                    <Table.Th w={110}>Reach</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>{byDay(upcoming)}</Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}

        {plan?.truncated && (
          <Text c="dimmed" size="xs">
            The plan was truncated — more windows exist than are published here.
          </Text>
        )}
      </Stack>

      {/* --------------------------------------------------------- history */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Recent capture windows
          </Title>
          <Text c="dimmed" size="sm">
            Both halves of the pipeline have to be running for a window to be covered: capture
            alone fills the raw log and produces nothing anybody can look at, and interpretation
            alone has nothing to read.
          </Text>
        </div>

        {captures?.length === 0 ? (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center" size="sm">
              No capture has been attempted yet.
            </Text>
          </Paper>
        ) : (
          <Paper withBorder>
            <Table.ScrollContainer minWidth={760}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={190}>Window</Table.Th>
                    <Table.Th>Event</Table.Th>
                    <Table.Th w={110}>Status</Table.Th>
                    <Table.Th w={90}>Attempts</Table.Th>
                    <Table.Th w={120}>Last seen</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(captures ?? []).map((r) => (
                    <CaptureRow key={r.id} run={r} now={now} />
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}
      </Stack>

      {/* ---------------------------------------------------------- alerts */}
      <Stack gap="sm">
        <div>
          <Title order={2} size="h5">
            Alerts sent
          </Title>
          <Text c="dimmed" size="sm">
            What was actually emailed, and when. An alert that fired and then resolved is a
            different story from one that is still true, so these sit beside the current state
            rather than replacing it.
          </Text>
        </div>

        {alerts?.length === 0 ? (
          <Paper withBorder p="xl">
            <Text c="dimmed" ta="center" size="sm">
              Nothing has been emailed.
            </Text>
          </Paper>
        ) : (
          <Paper withBorder>
            <Table.ScrollContainer minWidth={620}>
              <Table verticalSpacing="xs" horizontalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th w={170}>Sent</Table.Th>
                    <Table.Th>Subject</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {(alerts ?? []).map((a) => (
                    <Table.Tr key={a.key}>
                      <Table.Td>
                        <Text size="sm" c="dimmed">
                          {a.lastSentAt ? when(a.lastSentAt) : '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{a.subject}</Text>
                        {a.body && (
                          <Text size="xs" c="dimmed" lineClamp={2}>
                            {a.body}
                          </Text>
                        )}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          </Paper>
        )}
      </Stack>
    </Stack>
  )
}

// ------------------------------------------------------------------ parts

function Verdict({ health, now }: { health: ReturnType<typeof useOpsHealth>; now: Date }) {
  const generated = health.plan?.generatedAt

  return (
    <Paper withBorder p="lg">
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <StatusDot tone={health.tone} size={14} />
          <div>
            <Text fw={600} size="lg">
              {health.verdict}
            </Text>
            <Text c="dimmed" size="sm">
              {health.tone === 'ok'
                ? 'Every scheduled job has reported within its cadence.'
                : health.tone === 'busy'
                  ? `${health.openWindows.length} capture window${health.openWindows.length === 1 ? '' : 's'} open.`
                  : 'Details below.'}
            </Text>
          </div>
        </Group>
        <Text c="dimmed" size="xs" ta="right">
          {generated ? (
            <>
              Plan published {ago((now.getTime() - generated.getTime()) / 1000)} ago
            </>
          ) : (
            'No plan published'
          )}
        </Text>
      </Group>
    </Paper>
  )
}

const STATE_COLOUR: Record<JobState, string> = {
  ok: 'gray',
  running: 'green',
  idle: 'gray',
  late: 'orange',
  stalled: 'red',
  failed: 'red',
  never: 'red',
}

const STATE_LABEL: Record<JobState, string> = {
  ok: 'ok',
  running: 'running',
  idle: 'idle',
  late: 'overdue',
  stalled: 'stalled',
  failed: 'failed',
  never: 'never run',
}

function JobRow({ health, now }: { health: JobHealth; now: Date }) {
  const { spec, latest, state } = health
  return (
    <Table.Tr>
      <Table.Td>
        <Tooltip label={spec.what} multiline w={280} withArrow>
          <Text size="sm" fw={600}>
            {spec.label}
          </Text>
        </Tooltip>
        <Text size="xs" c="dimmed">
          {spec.kind === 'scheduled' ? spec.schedule : 'on demand'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Badge color={STATE_COLOUR[state]} variant={health.bad ? 'filled' : 'light'} size="sm">
          {STATE_LABEL[state]}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {latest ? `${ago((now.getTime() - latest.startedAt.getTime()) / 1000)} ago` : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{health.detail}</Text>
        {latest && <Counters run={latest} />}
      </Table.Td>
    </Table.Tr>
  )
}

function Counters({ run }: { run: JobRun }) {
  const entries = Object.entries(run.counters ?? {}).filter(([, v]) => v !== 0)
  if (entries.length === 0) return null
  return (
    <Group gap={10} mt={4}>
      {entries.map(([k, v]) => (
        <Text key={k} size="xs" c="dimmed" ff="monospace">
          {k} {v.toLocaleString()}
        </Text>
      ))}
    </Group>
  )
}

/**
 * One open window, and the evidence that it is working.
 *
 * The capture run says which executions were started; the job runs say what
 * those executions have actually done since. Both, because the first without
 * the second is the reassuring half of a wedged pipeline.
 */
function OpenWindow({
  window: w,
  now,
  captures,
}: {
  window: PlannedWindow
  now: Date
  captures: CaptureRun[]
}) {
  const runs = useWindowRuns(w.id)
  const capture = captures.find((c) => c.id === w.id)
  const half = (job: string) => runs.find((r) => r.job === job) ?? null

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" align="flex-start" wrap="nowrap" mb="sm">
        <div>
          <Text fw={600}>{w.eventName}</Text>
          <Text size="sm" c="dimmed">
            {w.trackId} · until {clock(w.end)} · requested by {w.attached.join(', ') || 'nobody'}
          </Text>
        </div>
        {capture ? (
          <Badge
            color={capture.status === 'running' ? 'green' : capture.status === 'abandoned' ? 'red' : 'gray'}
            variant="light"
          >
            {capture.status}
          </Badge>
        ) : (
          <Badge color="red">not started</Badge>
        )}
      </Group>

      {!capture && (
        <Alert color="red" variant="light" mb="sm">
          The window is open and the reconciler has recorded no capture for it. Nothing is being
          recorded, and RIS has no history API to recover it from afterwards.
        </Alert>
      )}

      <Stack gap="xs">
        <Half label="Capture" run={half('ris-capture')} now={now} />
        <Half label="Interpret" run={half('interpret')} now={now} />
      </Stack>
    </Paper>
  )
}

function Half({ label, run, now }: { label: string; run: JobRun | null; now: Date }) {
  if (!run) {
    return (
      <Group gap="xs" wrap="nowrap">
        <Text size="sm" fw={600} w={80}>
          {label}
        </Text>
        <Text size="sm" c="red">
          has not reported at all
        </Text>
      </Group>
    )
  }
  const since = (now.getTime() - run.heartbeatAt.getTime()) / 1000
  const stale = since > 120
  return (
    <div>
      <Group gap="xs" wrap="nowrap" align="baseline">
        <Text size="sm" fw={600} w={80}>
          {label}
        </Text>
        <Text size="sm" c={stale ? 'red' : undefined}>
          {run.summary ?? run.status}
        </Text>
        <Text size="xs" c={stale ? 'red' : 'dimmed'}>
          {stale ? `no report for ${ago(since)}` : `reporting, ${ago(since)} ago`}
        </Text>
      </Group>
      <Counters run={run} />
      {(run.notes ?? []).length > 0 && (
        <Stack gap={2} mt={4} ml={88}>
          {run.notes?.map((n) => (
            <Text key={n} size="xs" c="dimmed" ff="monospace">
              {n}
            </Text>
          ))}
        </Stack>
      )}
    </div>
  )
}

function CaptureRow({ run, now }: { run: CaptureRun; now: Date }) {
  const colour =
    run.status === 'running' ? 'green' : run.status === 'abandoned' ? 'red' : 'gray'
  return (
    <Table.Tr>
      <Table.Td>
        <Text size="sm">{run.windowStart ? when(run.windowStart) : '—'}</Text>
        <Text size="xs" c="dimmed">
          to {run.windowEnd ? clock(run.windowEnd) : '—'}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm">{run.eventName}</Text>
        {run.note && (
          <Text size="xs" c="red">
            {run.note}
          </Text>
        )}
      </Table.Td>
      <Table.Td>
        <Badge color={colour} variant="light" size="sm">
          {run.status}
        </Badge>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c={run.attempts > 1 ? 'orange' : 'dimmed'}>
          {run.attempts}
        </Text>
      </Table.Td>
      <Table.Td>
        <Text size="sm" c="dimmed">
          {run.lastSeenAt ? `${ago((now.getTime() - run.lastSeenAt.getTime()) / 1000)} ago` : '—'}
        </Text>
      </Table.Td>
    </Table.Tr>
  )
}

/**
 * Windows grouped under the day they fall on.
 *
 * A race weekend is three or four windows a day, and an ungrouped list of them
 * reads as a wall of near-identical rows in which a missing day is invisible.
 */
function byDay(windows: PlannedWindow[]) {
  const days = new Map<string, PlannedWindow[]>()
  for (const w of windows) {
    const key = w.start.toLocaleDateString(undefined, {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
    })
    const list = days.get(key)
    if (list) list.push(w)
    else days.set(key, [w])
  }

  return [...days].map(([day, list]) => (
    <Fragment key={day}>
      <Table.Tr>
        <Table.Td colSpan={4} style={{ background: 'var(--mantine-color-default-hover)' }}>
          <Text size="xs" fw={700} tt="uppercase" style={{ letterSpacing: '0.04em' }}>
            {day}
          </Text>
        </Table.Td>
      </Table.Tr>
      {list.map((w) => (
        <Table.Tr key={w.id}>
          <Table.Td>
            <Text size="sm">
              {clock(w.start)} – {clock(w.end)}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm">{w.eventName}</Text>
            <Text size="xs" c="dimmed">
              {w.trackId}
              {w.channels.length > 0 && ` · ${w.channels.join(', ')}`}
            </Text>
          </Table.Td>
          <Table.Td>
            <Text size="sm" c="dimmed">
              {w.attached.join(', ') || '—'}
            </Text>
          </Table.Td>
          <Table.Td>
            <Badge size="sm" variant="light" color={w.actionable ? 'green' : 'gray'}>
              {w.actionable ? 'in reach' : 'planned'}
            </Badge>
          </Table.Td>
        </Table.Tr>
      ))}
    </Fragment>
  ))
}

function nextWindowLine(upcoming: PlannedWindow[], now: Date): string {
  const next = upcoming.find((w) => w.start > now)
  if (!next) return 'Nothing is planned within the horizon.'
  return `Next is ${next.eventName} at ${when(next.start)}.`
}

function when(d: Date): string {
  return d.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function clock(d: Date): string {
  return d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
}

/**
 * Which build is deployed, and whether anything is still running an older one.
 *
 * A Cloud Run Job pins its image when the execution starts, so a job that began
 * before the last deploy keeps running the previous build until it ends. That
 * is how Cloud Run works rather than a fault — but it is invisible, and twice in
 * one weekend it made a deployed fix look as though it had not worked. Stating
 * it turns "why is the old behaviour still happening" into something readable
 * rather than something to be deduced from document ids.
 */
function DeployedBuildLine({ now }: { now: Date }) {
  const deployed = useDeployedBuild()
  const runs = useLatestJobRuns()
  if (!deployed) return null

  const stale = [...runs.values()].filter(
    (r) => r.status === 'running' && isStaleBuild(r, deployed.build),
  )

  return (
    <Group gap="sm" align="baseline" wrap="wrap">
      <Text size="sm" c="dimmed">
        Deployed build
      </Text>
      <Code>{deployed.build}</Code>
      {deployed.deployedAt && (
        <Text size="xs" c="dimmed">
          {ago((now.getTime() - deployed.deployedAt.getTime()) / 1000)} ago
        </Text>
      )}
      {stale.length > 0 && (
        <Badge color="yellow" variant="light">
          {stale.length === 1
            ? `${stale[0]?.job} is running an older build`
            : `${stale.length} jobs running an older build`}
        </Badge>
      )}
    </Group>
  )
}
